import {Box, Tabs, Tab} from "@mui/material";
import * as React from 'react';
import TabPanel from "../../../../share/components/tab/TabPanel";
import { Provider } from "../../../types/models/Provider";
import ProviderUserListItem from "../../../../timber/components/providerUser/ProviderUserList/ProviderUserListItem";
import CuttingListItem from "../../../../timber/components/cutting/CuttingList/CuttingListItem";
import ProviderOverviewTab from "./ProviderOverviewTab";
import ProviderSplitTab from "./ProviderSplitTab";
import {useStateSession} from "../../../../hooks/useAuth";
import { t } from "@lingui/core/macro";
import GenericRootReport, {defaultFormOptions} from "../../generic/report/GenericRootReport";
import TimelineRootReport from "../../generic/report/TimelineRootReport";
import PriceCalculator from "../../generic/priceCalculator/PriceCalculator.tsx";
import {startOfMonth, endOfMonth} from "date-fns";

type IFormProps = {
    data: Provider;
};

// The group-by and timeline endpoints behind the overview accept a report
// permission on any one of these entities, so the tab is shown for whoever can
// read at least one of them. The plain provider role reaches them through
// Shipments/* and TimberStorages/*, which is why Cuttings alone is not enough.
const reportPermissions = [
    'applications/timber/entities/Cuttings/report/view',
    'applications/timber/entities/TimberStorages/report/view',
    'applications/timber/entities/Shipments/report/view',
    'applications/timber/entities/Buyers/report/view',
    'applications/timber/entities/ForestOwners/report/view',
];

const ProviderTabs: React.FC<IFormProps> = ({ data = {} }) => {
    const session = useStateSession()
    const searchParams = new URLSearchParams(window.location.search)

    const canReport = reportPermissions.some(permission => session.hasPermission('', permission));

    const [tabValue, setTabValue] = React.useState(searchParams.get('tab') || 'providerUser');
    const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {

        if (searchParams.has('tab')) {
            searchParams.set('tab', newValue)
        } else {
            searchParams.append('tab', newValue)
        }
        window.history.pushState({}, '', '?' + searchParams.toString())

        setTabValue(newValue);
    };

    const providerId = data?.id;

    // The report is about the provider and nothing else, so the provider
    // scopes it on the server. The cutting pick stays out of the way: choosing
    // a cutting of another provider would only widen the scope.
    const customizeFiler = React.useCallback((filter: Record<string, any>) => ({
        ...filter,
        providerId: providerId ? [providerId] : undefined,
    }), [providerId]);

    const formOptions = {
        ...defaultFormOptions,
        showCutting: false,
    }

    // The timeline reads its scope from the filter the same way the report
    // does, so it gets the same provider scope.
    const timelineFilter = React.useMemo(
        () => ({providerId: providerId ? [providerId] : undefined}),
        [providerId]
    );

    const reportTitle = `Provider ${data?.name || ''}`;
    const now = new Date();

    // The date pickers hand out Date objects, and the filter is built from
    // them with `formatDateTimeParam`. A pre-formatted string would be
    // dropped by that function and the date range would quietly do nothing.
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);

    return (
        <Box sx={ { width: '100%' } }>
            <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                data-testid="Provider-tabs"
            >
                { session.hasPermission('', 'applications/timber/entities/ProviderUsers/list/view') &&
                    <Tab label={t`Provider Users`} value="providerUser"/>
                }
                { session.hasPermission('', 'applications/timber/entities/Cuttings/list/view') &&
                    <Tab label={t`Cuttings`} value="cutting"/>
                }
                { canReport &&
                    <Tab label={t`Overview`} value="overview"/>
                }
                { canReport &&
                    <Tab label={t`Split`} value="split"/>
                }
                { canReport &&
                    <Tab label={t`Report All`} value="reportAll"/>
                }
                { canReport &&
                    <Tab label={t`Report Not shipt`} value="reportNotShipt"/>
                }
                { canReport &&
                    <Tab label={t`Report Shipt`} value="reportShipt"/>
                }
                { canReport &&
                    <Tab label={t`Timeline Report`} value="timelineReport"/>
                }
                { session.hasPermission('', 'applications/timber/entities/PricingProfiles/view') &&
                    <Tab label={t`Price Calculator`} value="priceCalculatorReport"/>
                }
                </Tabs>

            { session.hasPermission('', 'applications/timber/entities/ProviderUsers/list/view') &&
                <TabPanel value={tabValue} index="providerUser" data-testid="ProviderUser-tab">
                    <ProviderUserListItem providerId={data?.id} showAdd={ true } />
                </TabPanel>
            }
            { session.hasPermission('', 'applications/timber/entities/Cuttings/list/view') &&
                <TabPanel value={tabValue} index="cutting" data-testid="Cutting-tab">
                    <CuttingListItem providerId={data?.id} showAdd={ true } />
                </TabPanel>
            }
            { canReport &&
                <TabPanel value={tabValue} index="overview" data-testid="Provider-overview-tab">
                    <ProviderOverviewTab providerId={providerId}/>
                </TabPanel>
            }
            { canReport &&
                <TabPanel value={tabValue} index="split" data-testid="Provider-split-tab">
                    <ProviderSplitTab providerId={providerId}/>
                </TabPanel>
            }
            { canReport &&
                <TabPanel value={tabValue} index="reportAll" data-testid="Provider-reportAll-tab">
                    <GenericRootReport
                        shipmentDefined={null}
                        mainTitle={reportTitle}
                        customizeFiler={customizeFiler}
                        formOptions={formOptions}
                        initialState={
                            {
                                startCreate: monthStart,
                                endCreate: monthEnd,
                            }
                        }
                    />
                </TabPanel>
            }
            { canReport &&
                <TabPanel value={tabValue} index="reportNotShipt" data-testid="Provider-reportNotShipt-tab">
                    <GenericRootReport
                        shipmentDefined={false}
                        mainTitle={reportTitle}
                        customizeFiler={customizeFiler}
                        formOptions={formOptions}
                        initialState={
                            {
                                startCreate: monthStart,
                                endCreate: monthEnd,
                            }
                        }
                    />
                </TabPanel>
            }
            { canReport &&
                <TabPanel value={tabValue} index="reportShipt" data-testid="Provider-reportShipt-tab">
                    <GenericRootReport
                        shipmentDefined={true}
                        mainTitle={reportTitle}
                        customizeFiler={customizeFiler}
                        formOptions={formOptions}
                        initialState={
                            {
                                startCreate: monthStart,
                                endCreate: monthEnd,
                                startShipment: monthStart,
                                endShipment: monthEnd,
                            }
                        }
                    />
                </TabPanel>
            }
            { canReport &&
                <TabPanel value={tabValue} index="timelineReport" data-testid="Provider-timelineReport-tab">
                    <TimelineRootReport
                        filter={timelineFilter}
                        showCutting={false}
                        showStorage={false}
                        showForm={false}
                        initialState={
                            {
                                startCreate: monthStart,
                                endCreate: monthEnd,
                                startShipment: monthStart,
                                endShipment: monthEnd,
                            }
                        }
                    />
                </TabPanel>
            }
            { session.hasPermission('', 'applications/timber/entities/PricingProfiles/view') &&
                <TabPanel value={tabValue} index="priceCalculatorReport" data-testid="Provider-priceCalculator-tab">
                    <PriceCalculator
                        shipmentDefined={null}
                        customizeFiler={customizeFiler}
                        formOptions={formOptions}
                        // No starting dates on purpose: the estimate is about
                        // what the provider has, so it covers all of it and
                        // says the same volume the overview reports. The date
                        // pickers on the form narrow it down.
                    />
                </TabPanel>
            }

        </Box>
    )
}

export default ProviderTabs;
